import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../contexts/tenant-context";
import ConfirmDialog from "../../components/common/confirm-dialog";
import { FileUploadArea } from "../../components/documents/upload/file-upload-area";
import { useSnackbar } from "../../contexts/snackbar-context";
import {
  BugAttachmentDto,
  BugCategory,
  BugLogDto,
  BugPriority,
  BugStatus,
  useBugLogService,
} from "../../services/bugLogService";
import { useTenantManagementService } from "../../services/tenantManagementService";
import BugReportListTable from "../../components/admin/bug-report-list-table";

const CATEGORIES: BugCategory[] = [
  "Bug Report",
  "Feature Request",
  "Comment/General Feedback",
];

const PRIORITIES: BugPriority[] = ["Low", "Medium", "High", "Critical"];
const STATUSES: BugStatus[] = ["In Progress", "Dev Done", "Done", "UAT"];
const STATUS_COLORS: Record<BugStatus, string> = {
  "In Progress": "#ffb74d",
  "Dev Done": "#4fc3f7",
  Done: "#81c784",
  UAT: "#ffd166",
};
const ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.pdf,.txt,.xls,.xlsx";
const MAX_ATTACHMENT_SIZE_MB = 10;
const ALLOWED_ATTACHMENT_EXTENSIONS = [
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".webp",
  ".heic",
  ".heif",
  ".pdf",
  ".txt",
  ".xls",
  ".xlsx",
];
const BUG_LIST_REFRESH_KEY = "bugReportListRefreshToken";

const getAttachmentDisplayName = (attachmentUrl?: string) => {
  if (!attachmentUrl) return "Attachment";

  try {
    const url = new URL(attachmentUrl);
    const lastSegment = url.pathname.split("/").filter(Boolean).pop();
    if (!lastSegment) return "Attachment";
    return decodeURIComponent(lastSegment);
  } catch {
    const lastSegment = attachmentUrl.split("/").filter(Boolean).pop();
    return lastSegment || attachmentUrl;
  }
};

const BugReportListPage: React.FC = () => {
  const navigate = useNavigate();
  const { isGlobalAdmin, selectedTenantId } = useTenant();
  const { showSuccess, showError } = useSnackbar();
  const {
    getBugLogs,
    updateBugLog,
    deleteBugLog,
    uploadBugLogAttachments,
    getBugLogAttachments,
    getBugLogAttachmentMetadata,
    deleteBugLogAttachment,
    deleteBugLogAttachments,
  } = useBugLogService();
  const { getTenants } = useTenantManagementService();
  const [reports, setReports] = useState<BugLogDto[]>([]);
  const [tenantNameById, setTenantNameById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingReport, setEditingReport] = useState<BugLogDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<BugLogDto | null>(null);

  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState<BugCategory>("Bug Report");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState<BugPriority>("Medium");
  const [editStatus, setEditStatus] = useState<BugStatus>("In Progress");
  const [editAttachmentFiles, setEditAttachmentFiles] = useState<File[]>([]);
  const [isDraggingEditAttachment, setIsDraggingEditAttachment] = useState(false);
  const [currentAttachments, setCurrentAttachments] = useState<BugAttachmentDto[]>([]);
  const [attachmentIdsPendingRemoval, setAttachmentIdsPendingRemoval] = useState<string[]>([]);
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string | null>(null);
  const [removeCurrentAttachment, setRemoveCurrentAttachment] = useState(false);

  const isGlobalAdminContext = isGlobalAdmin && selectedTenantId === "__GLOBAL_ADMIN__";

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getBugLogs();
      setReports(data);
    } catch (err: any) {
      showError(err?.message || "Failed to load bug reports.");
    } finally {
      setLoading(false);
    }
  }, [getBugLogs, showError]);

  const loadTenantNames = useCallback(async () => {
    if (!isGlobalAdminContext) {
      setTenantNameById({});
      return;
    }

    try {
      const tenants = await getTenants();
      const nextMap = tenants.reduce<Record<string, string>>((acc, tenant) => {
        if (tenant.id) {
          acc[tenant.id] = tenant.name;
        }
        return acc;
      }, {});

      setTenantNameById(nextMap);
    } catch {
      // Keep GUID fallback in table if tenant names cannot be loaded.
      setTenantNameById({});
    }
  }, [getTenants, isGlobalAdminContext]);
  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    void loadTenantNames();
  }, [loadTenantNames]);
  useEffect(() => {
    const refreshFromStorage = (event: StorageEvent) => {
      if (event.key === BUG_LIST_REFRESH_KEY) {
        void loadReports();
      }
    };

    window.addEventListener("storage", refreshFromStorage);
    return () => {
      window.removeEventListener("storage", refreshFromStorage);
    };
  }, [loadReports]);

  const openAttachmentViewer = (attachmentId: string) => {
    window.open(
      `/bug-report/attachment/${attachmentId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const isSupportedAttachment = (file: File) => {
    const lowerName = file.name.toLowerCase();
    const hasAllowedExtension = ALLOWED_ATTACHMENT_EXTENSIONS.some((ext) =>
      lowerName.endsWith(ext)
    );
    const hasAllowedMimeType =
      file.type.startsWith("image/") ||
      file.type === "application/pdf" ||
      file.type === "text/plain" ||
      file.type === "application/vnd.ms-excel" ||
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    return hasAllowedExtension || hasAllowedMimeType;
  };

  const clearEditAttachment = (index: number) => {
    setEditAttachmentFiles((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const clearAllEditAttachments = () => {
    setEditAttachmentFiles([]);
  };

  const validateEditAttachment = (file: File): string | null => {
    if (!isSupportedAttachment(file)) {
      return "Please attach a supported file (PNG/JPG/GIF/WebP/HEIC/PDF/TXT/XLS/XLSX).";
    }

    if (file.size > MAX_ATTACHMENT_SIZE_MB * 1024 * 1024) {
      return `Attachment must be ${MAX_ATTACHMENT_SIZE_MB}MB or smaller.`;
    }

    return null;
  };

  const handleEditAttachmentSelection = (files: FileList) => {
    const selected = Array.from(files);
    if (selected.length === 0) return;

    const nextFiles: File[] = [];
    for (const file of selected) {
      const validationError = validateEditAttachment(file);
      if (validationError) {
        showError(validationError);
        return;
      }
      nextFiles.push(file);
    }

    setEditAttachmentFiles((prev) => [...prev, ...nextFiles]);
  };

  const handleEditAttachmentDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingEditAttachment(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleEditAttachmentSelection(e.dataTransfer.files);
    }
  };

  const handleEditAttachmentDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDraggingEditAttachment(true);
  };

  const handleEditAttachmentDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDraggingEditAttachment(false);
    }
  };

  const handleOpenNewBugReport = () => {
    navigate("/bug-report", {
      state: { returnTo: "/app-management/bug-reports" },
    });
  };

  const openEditDialog = (report: BugLogDto) => {
    setEditingReport(report);
    setEditTitle(report.title);
    setEditCategory(report.category);
    setEditDescription(report.descriptionStepToProduce);
    setEditPriority(report.priority);
    setEditStatus(report.status);
    clearAllEditAttachments();
    setAttachmentIdsPendingRemoval([]);
    setRemoveCurrentAttachment(false);
    setCurrentAttachments([]);
    setCurrentAttachmentName(report.attachmentUrl ? getAttachmentDisplayName(report.attachmentUrl) : null);

    void getBugLogAttachments(report.id)
      .then((attachments) => {
        setCurrentAttachments(attachments);
      })
      .catch(() => {
        setCurrentAttachments([]);
      });

    if (report.attachmentUrl) {
      void getBugLogAttachmentMetadata(report.id)
        .then((metadata) => {
          setCurrentAttachmentName(metadata.fileName || getAttachmentDisplayName(report.attachmentUrl));
        })
        .catch(() => {
          // Keep fallback name when metadata cannot be fetched.
        });
    }
  };

  const closeEditDialog = () => {
    if (isSavingEdit) return;
    setEditingReport(null);
    setCurrentAttachments([]);
    setAttachmentIdsPendingRemoval([]);
    setCurrentAttachmentName(null);
    setRemoveCurrentAttachment(false);
    clearAllEditAttachments();
  };

  const markAttachmentForRemoval = (attachmentId: string) => {
    setAttachmentIdsPendingRemoval((prev) =>
      prev.includes(attachmentId) ? prev : [...prev, attachmentId]
    );
  };

  const visibleCurrentAttachments = currentAttachments.filter(
    (attachment) => !attachmentIdsPendingRemoval.includes(attachment.attachmentId)
  );

  const saveEdit = async () => {
    if (!editingReport) return;

    if (!editTitle.trim() || !editDescription.trim()) {
      showError("Title and description are required.");
      return;
    }

    try {
      setIsSavingEdit(true);

      await updateBugLog({
        id: editingReport.id,
        title: editTitle.trim(),
        category: editCategory,
        descriptionStepToProduce: editDescription.trim(),
        priority: editPriority,
        status: editStatus,
        assignedToUserId: editingReport.assignedToUserId,
        auditedByUserId: editingReport.auditedByUserId,
      });

      if (attachmentIdsPendingRemoval.length > 0) {
        for (const attachmentId of attachmentIdsPendingRemoval) {
          await deleteBugLogAttachment(attachmentId);
        }
      }

      if (removeCurrentAttachment) {
        await deleteBugLogAttachments(editingReport.id);
      }

      if (editAttachmentFiles.length > 0) {
        await uploadBugLogAttachments(editingReport.id, editAttachmentFiles);
      }

      showSuccess("Bug report updated successfully.");
      setEditingReport(null);
      setCurrentAttachments([]);
      setAttachmentIdsPendingRemoval([]);
      setCurrentAttachmentName(null);
      setRemoveCurrentAttachment(false);
      clearAllEditAttachments();
      await loadReports();
    } catch (err: any) {
      showError(err?.message || "Failed to update bug report.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const requestDelete = (report: BugLogDto) => {
    setPendingDelete(report);
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    try {
      setIsDeleting(true);

      const deleted = await deleteBugLog(pendingDelete.id);
      if (!deleted) {
        showError("Failed to delete bug report.");
        return;
      }

      showSuccess("Bug report deleted successfully.");
      setPendingDelete(null);
      await loadReports();
    } catch (err: any) {
      showError(err?.message || "Failed to delete bug report.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
        }}
      >
        <Box>
        <Typography variant="h5" fontWeight={700} color="#fff">
          Bug Report
        </Typography>
        <Typography variant="body2" color="#bbb" sx={{ mt: 0.5 }}>
          {isGlobalAdminContext
            ? "Showing bug report list across all tenants"
            : "Showing bug report list for your current tenant"}
        </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenNewBugReport}
            sx={{
              bgcolor: "hsl(19 101% 51% / 1)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              width: { xs: "100%", sm: "auto" },
              "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" },
            }}
          >
            Report New Bug
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box
          sx={{
            minHeight: 240,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <BugReportListTable
          reports={reports}
          loading={loading}
          isGlobalAdminContext={isGlobalAdminContext}
          tenantNameById={tenantNameById}
          onEdit={openEditDialog}
          onDelete={requestDelete}
        />
      )}

      <Dialog
        open={!!editingReport}
        onClose={closeEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#111",
            color: "#fff",
            border: "1px solid #333",
          },
        }}
      >
        {isSavingEdit && <LinearProgress sx={{ backgroundColor: "#222" }} />}
        <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)", fontWeight: 700 }}>
          Edit Bug Report
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {isSavingEdit && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Saving bug report. Please wait...
            </Alert>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
              disabled={isSavingEdit}
              inputProps={{ maxLength: 200 }}
              sx={inputSx}
            />
            <FormControl fullWidth sx={selectFormSx}>
              <InputLabel sx={{ color: "#aaa" }}>Category</InputLabel>
              <Select
                value={editCategory}
                label="Category"
                onChange={(e) => setEditCategory(e.target.value as BugCategory)}
                disabled={isSavingEdit}
                sx={selectSx}
              >
                {CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl sx={{ flex: 1, minWidth: 180, ...selectFormSx }}>
                <InputLabel sx={{ color: "#aaa" }}>Priority</InputLabel>
                <Select
                  value={editPriority}
                  label="Priority"
                  onChange={(e) => setEditPriority(e.target.value as BugPriority)}
                  disabled={isSavingEdit}
                  sx={selectSx}
                >
                  {PRIORITIES.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ flex: 1, minWidth: 180, ...selectFormSx }}>
                <InputLabel sx={{ color: "#aaa" }}>Status</InputLabel>
                <Select
                  value={editStatus}
                  label="Status"
                  onChange={(e) => setEditStatus(e.target.value as BugStatus)}
                  disabled={isSavingEdit}
                  sx={selectSx}
                  renderValue={(val) => (
                    <Box
                      component="span"
                      sx={{
                        color: STATUS_COLORS[val as BugStatus],
                        fontWeight: 700,
                      }}
                    >
                      {val}
                    </Box>
                  )}
                >
                  {STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      <Box
                        component="span"
                        sx={{ color: STATUS_COLORS[status], fontWeight: 700, mr: 1 }}
                      >
                        ●
                      </Box>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Description / Steps to Produce"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              disabled={isSavingEdit}
              multiline
              minRows={5}
              maxRows={12}
              inputProps={{ maxLength: 3000 }}
              sx={inputSx}
            />

            <Box>
              <Typography fontSize={13} color="#aaa" mb={1}>
                Attachment (Image, PDF, TXT, Excel)
              </Typography>

              {visibleCurrentAttachments.length > 0 && (
                <Box sx={{ mb: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
                  {visibleCurrentAttachments.map((attachment) => (
                    <Box
                      key={attachment.attachmentId}
                      sx={{
                        px: 1.5,
                        py: 1,
                        border: "1px solid #333",
                        borderRadius: 1,
                        bgcolor: "#171717",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="#ddd"
                        sx={{ overflowWrap: "anywhere", flex: 1, minWidth: 0 }}
                      >
                        {attachment.fileName}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
                        <IconButton
                          size="small"
                          onClick={() => openAttachmentViewer(attachment.attachmentId)}
                          disabled={isSavingEdit}
                          aria-label="view attachment"
                          sx={{
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => markAttachmentForRemoval(attachment.attachmentId)}
                          disabled={isSavingEdit}
                          aria-label="remove current attachment"
                          sx={{
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(200,50,50,0.8)" },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {visibleCurrentAttachments.length === 0 && currentAttachments.length === 0 && editingReport?.attachmentUrl && !removeCurrentAttachment && (
                <Box
                  sx={{
                    mb: 1.5,
                    px: 1.5,
                    py: 1,
                    border: "1px solid #333",
                    borderRadius: 1,
                    bgcolor: "#171717",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="#ddd"
                    sx={{ overflowWrap: "anywhere", flex: 1, minWidth: 0 }}
                  >
                    Current: {currentAttachmentName || getAttachmentDisplayName(editingReport.attachmentUrl)}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, ml: "auto" }}>
                    <IconButton
                      size="small"
                      onClick={() => openAttachmentViewer(editingReport.id)}
                      disabled={isSavingEdit}
                      aria-label="view attachment"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setRemoveCurrentAttachment(true)}
                      disabled={isSavingEdit}
                      aria-label="remove current attachment"
                      sx={{
                        bgcolor: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        "&:hover": { bgcolor: "rgba(200,50,50,0.8)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}

              {attachmentIdsPendingRemoval.length > 0 && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {attachmentIdsPendingRemoval.length} attachment{attachmentIdsPendingRemoval.length > 1 ? "s" : ""} will be removed when you save.
                </Alert>
              )}

              {removeCurrentAttachment && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Current attachment will be removed when you save.
                </Alert>
              )}

              <FileUploadArea
                isDragging={isDraggingEditAttachment}
                onDrop={handleEditAttachmentDrop}
                onDragOver={handleEditAttachmentDragOver}
                onDragLeave={handleEditAttachmentDragLeave}
                onFileSelect={handleEditAttachmentSelection}
                primaryText="Replace current attachment with a new image, PDF, text, or Excel file."
                secondaryText="Drag and drop files, or click to browse."
                dragText="Drag & drop replacement file here"
                dropText="Drop replacement files here"
                browseText="or click to browse files"
                accept={ATTACHMENT_ACCEPT}
                multiple={true}
                maxInfoLabel={`Max ${MAX_ATTACHMENT_SIZE_MB}MB per file`}
                compact={true}
                disabled={isSavingEdit}
              />

              {editAttachmentFiles.length > 0 && (
                <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={clearAllEditAttachments}
                    disabled={isSavingEdit}
                    sx={{ alignSelf: "flex-end", color: "#ff8a65", textTransform: "none" }}
                  >
                    Remove all selected
                  </Button>
                  {editAttachmentFiles.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        border: "1px solid #333",
                        borderRadius: 1,
                        p: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box>
                        <Typography variant="body2" color="#fff" fontWeight={600}>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="#bbb">
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => clearEditAttachment(index)}
                        disabled={isSavingEdit}
                        sx={{
                          bgcolor: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          "&:hover": { bgcolor: "rgba(200,50,50,0.8)" },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={isSavingEdit} sx={{ color: "#fff" }}>
            Cancel
          </Button>
          <Button
            onClick={saveEdit}
            variant="contained"
            disabled={isSavingEdit}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 1)",
              "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            }}
          >
            {isSavingEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete Bug Report"
        message={
          pendingDelete
            ? `Are you sure you want to delete "${pendingDelete.title}"? This action cannot be undone.`
            : "Are you sure you want to delete this bug report?"
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </Container>
  );
};

export default BugReportListPage;

const inputSx = {
  "& .MuiInputBase-root": { color: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 0.6)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 1)",
  },
  "& .MuiInputLabel-root": { color: "#aaa" },
  "& .MuiInputLabel-root.Mui-focused": { color: "hsl(19 101% 51% / 1)" },
};

const selectFormSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 0.6)",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "hsl(19 101% 51% / 1)",
  },
};

const selectSx = {
  color: "#fff",
  "& .MuiSvgIcon-root": { color: "#aaa" },
};